import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    whatsapp: '',
    treatment: '',
    preferredDate: '',
    preferredTime: '',
    message: ''
  });
  const [callbackFormData, setCallbackFormData] = useState({
    name: '',
    phone: '',
    preferredTime: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isCallbackSubmitting, setIsCallbackSubmitting] = useState(false);
  const [callbackSubmitStatus, setCallbackSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [showCallbackModal, setShowCallbackModal] = useState(false);
  const [showConsultationModal, setShowConsultationModal] = useState(false);
  const [sliderPositions, setSliderPositions] = useState<{ [key: number]: number }>({
    0: 50,
    1: 50,
    2: 50,
    3: 50
  });
  const [isDragging, setIsDragging] = useState<number | null>(null);

  // Handle scroll for navbar
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in-up');
        }
      });
    }, observerOptions);

    const sections = document.querySelectorAll('.scroll-animate');
    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCallbackInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setCallbackFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.treatment || !formData.preferredDate || !formData.preferredTime) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 3000);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('whatsapp', formData.whatsapp || '');
      formBody.append('treatment', formData.treatment);
      formBody.append('preferredDate', formData.preferredDate);
      formBody.append('preferredTime', formData.preferredTime);
      formBody.append('message', formData.message || '');

      const response = await fetch('https://readdy.ai/api/form/d5apo3ndcu97hjrn3mcg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', whatsapp: '', treatment: '', preferredDate: '', preferredTime: '', message: '' });
        setTimeout(() => {
          setShowConsultationModal(false);
          setSubmitStatus('idle');
        }, 3000);
      } else {
        setSubmitStatus('error');
        setTimeout(() => setSubmitStatus('idle'), 3000);
      }
    } catch (error) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCallbackSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!callbackFormData.name || !callbackFormData.phone) {
      setCallbackSubmitStatus('error');
      setTimeout(() => setCallbackSubmitStatus('idle'), 3000);
      return;
    }

    setIsCallbackSubmitting(true);
    setCallbackSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', callbackFormData.name);
      formBody.append('phone', callbackFormData.phone);
      formBody.append('preferredTime', callbackFormData.preferredTime || '');

      const response = await fetch('https://readdy.ai/api/form/callback-invisalign-beaulieu', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setCallbackSubmitStatus('success');
        setCallbackFormData({ name: '', phone: '', preferredTime: '' });
        setTimeout(() => {
          setShowCallbackModal(false);
          setCallbackSubmitStatus('idle');
        }, 3000);
      } else {
        setCallbackSubmitStatus('error');
        setTimeout(() => setCallbackSubmitStatus('idle'), 3000);
      }
    } catch (error) {
      setCallbackSubmitStatus('error');
      setTimeout(() => setCallbackSubmitStatus('idle'), 3000);
    } finally {
      setIsCallbackSubmitting(false);
    }
  };

  const handleConsultationClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setShowConsultationModal(true);
  };

  const handleHeroConsultationClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setShowConsultationModal(true);
  };

  const handleNavConsultationClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setShowConsultationModal(true);
  };

  const handleSliderMove = (index: number, e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    if (isDragging !== index) return;

    const container = e.currentTarget;
    const rect = container.getBoundingClientRect();
    const x = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const position = ((x - rect.left) / rect.width) * 100;
    
    setSliderPositions(prev => ({
      ...prev,
      [index]: Math.min(Math.max(position, 0), 100)
    }));
  };

  const handleSliderStart = (index: number) => {
    setIsDragging(index);
  };

  const handleSliderEnd = () => {
    setIsDragging(null);
  };

  useEffect(() => {
    if (isDragging !== null) {
      const handleMouseUp = () => setIsDragging(null);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchend', handleMouseUp);
      return () => {
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchend', handleMouseUp);
      };
    }
  }, [isDragging]);

  return (
    <div className="min-h-screen bg-white font-['Open_Sans']">
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in-up {
          animation: fadeInUp 0.8s ease-out forwards;
        }
        
        .scroll-animate {
          opacity: 0;
        }

        .before-after-slider {
          position: relative;
          overflow: hidden;
          cursor: ew-resize;
          user-select: none;
          -webkit-user-select: none;
        }

        .before-after-slider img {
          pointer-events: none;
        }
      `}</style>

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/0a3008c36638439fa5d134c9c6a19be7/f2c19796cf6e2cf808b2267d17a34318.png" 
                alt="Beaulieu Dental Logo" 
                className="h-24 w-auto object-contain"
              />
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#transformations" className={`text-sm font-medium transition-colors cursor-pointer hover:text-[#d0a24f] font-['Montserrat'] ${isScrolled ? 'text-[#444c42]' : 'text-white'}`}>Transformations</a>
              <a href="#value" className={`text-sm font-medium transition-colors cursor-pointer hover:text-[#d0a24f] font-['Montserrat'] ${isScrolled ? 'text-[#444c42]' : 'text-white'}`}>Why Invisalign</a>
              <a href="#features" className={`text-sm font-medium transition-colors cursor-pointer hover:text-[#d0a24f] font-['Montserrat'] ${isScrolled ? 'text-[#444c42]' : 'text-white'}`}>Features</a>
              <a href="#testimonials" className={`text-sm font-medium transition-colors cursor-pointer hover:text-[#d0a24f] font-['Montserrat'] ${isScrolled ? 'text-[#444c42]' : 'text-white'}`}>Testimonials</a>
              <a href="https://beaulieudental.co.uk/treatments/invisalign/" target="_blank" rel="noopener noreferrer" className={`text-sm font-medium transition-colors cursor-pointer hover:text-[#d0a24f] font-['Montserrat'] ${isScrolled ? 'text-[#444c42]' : 'text-white'}`}>Main Website</a>
              <a href="#consultation" onClick={handleNavConsultationClick} className="bg-[#d0a24f] text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-[#dcb964] transition-colors cursor-pointer whitespace-nowrap font-['Montserrat']">
                Book Free Consultation
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/a4e8f42d60c4d154d8f242ec6966492c.jpg"
            alt="Beaulieu Dental Clinic Springfield Chelmsford"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center w-full py-32">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-8 leading-tight font-['Montserrat']">
            <strong>Transform Your Smile</strong><br />with Invisalign in Chelmsford
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-10 max-w-3xl mx-auto leading-relaxed">
            Led by <strong>Dr George Papadakis</strong>, a <strong>Diamond Apex Award-winning Invisalign Elite Provider</strong>, recognised among the UK's leading Invisalign clinicians for precision-led, natural smile transformations, with <strong>0% interest-free finance available</strong>.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a href="#consultation" onClick={handleHeroConsultationClick} className="bg-[#d0a24f] text-white px-8 py-4 rounded-full text-lg font-bold hover:bg-[#dcb964] transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap shadow-xl font-['Montserrat']">
              Book Free Consultation
            </a>
            <button 
              onClick={() => setShowCallbackModal(true)}
              className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-full text-lg font-bold hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap border-2 border-white/30 font-['Montserrat']"
            >
              Request Call Back
            </button>
          </div>
          
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 transform transition-all duration-500 hover:scale-105 hover:bg-white/15">
              <div className="w-14 h-14 bg-[#d0a24f] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-computer-line text-2xl text-white"></i>
              </div>
              <h3 className="text-white font-semibold text-lg mb-2 font-['Montserrat']">iTero Lumina 3D Scanning</h3>
              <p className="text-white/80 text-sm">Accurate, fast, and no messy impressions</p>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 transform transition-all duration-500 hover:scale-105 hover:bg-white/15">
              <div className="w-14 h-14 bg-[#d0a24f] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-eye-line text-2xl text-white"></i>
              </div>
              <h3 className="text-white font-semibold text-lg mb-2 font-['Montserrat']">Preview Your Future Smile</h3>
              <p className="text-white/80 text-sm">Before starting treatment</p>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 transform transition-all duration-500 hover:scale-105 hover:bg-white/15">
              <div className="w-14 h-14 bg-[#d0a24f] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-2xl text-white"></i>
              </div>
              <h3 className="text-white font-semibold text-lg mb-2 font-['Montserrat']">Fast Results</h3>
              <p className="text-white/80 text-sm">See visible improvements in as little as 6-12 months</p>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 transform transition-all duration-500 hover:scale-105 hover:bg-white/15">
              <div className="w-14 h-14 bg-[#d0a24f] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-user-heart-line text-2xl text-white"></i>
              </div>
              <h3 className="text-white font-semibold text-lg mb-2 font-['Montserrat']">Custom Invisalign Aligners</h3>
              <p className="text-white/80 text-sm">Tailored to your needs</p>
            </div>
          </div>
        </div>
      </section>

      {/* 0% Finance Banner */}
      <section className="bg-gradient-to-r from-[#d0a24f] to-[#dcb964] py-8 scroll-animate">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-percent-line text-3xl text-[#d0a24f]"></i>
              </div>
              <div>
                <h3 className="text-white font-bold text-2xl mb-1 font-['Montserrat']">0% Interest-Free Finance Available</h3>
                <p className="text-white/90 text-sm">Make your <strong>Invisalign treatment in Chelmsford</strong> more affordable with flexible payment plans</p>
              </div>
            </div>
            <a href="#consultation" className="bg-white text-[#d0a24f] px-8 py-3 rounded-full font-bold hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap shadow-lg font-['Montserrat']">
              Learn More
            </a>
          </div>
        </div>
      </section>

      {/* Value Proposition Section - Restored Version 9 Layout */}
      <section id="value" className="py-16 bg-gradient-to-b from-gray-50 to-white scroll-animate">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#444c42] mb-6 font-['Montserrat']">
              Why Choose <strong>Invisalign at Beaulieu Dental, Springfield</strong>?
            </h2>
            <p className="text-xl text-[#7A8178] max-w-3xl mx-auto">
              Experience <strong>Invisalign treatment in Chelmsford</strong> with one of the UK's leading Invisalign providers, delivered by <strong>Dr George Papadakis</strong>, a <strong>Diamond Apex Award-winning Invisalign Elite Provider</strong>. Using advanced digital planning and a precision-led approach, Dr George focuses on predictable, natural smile transformations tailored to each patient.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="flex gap-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-[#d0a24f]/10 rounded-2xl flex items-center justify-center">
                    <i className="ri-trophy-line text-3xl text-[#d0a24f]"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-[#444c42] mb-3 font-['Montserrat']">Diamond Apex Award-Winning Expertise</h3>
                  <p className="text-[#7A8178] leading-relaxed">Your <strong>Invisalign treatment</strong> is personally overseen by <strong>Dr George Papadakis</strong>, one of a select group of UK clinicians to achieve <strong>Diamond Apex status</strong> — Invisalign's highest level of recognition for clinical experience, case volume, and consistent results.</p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-[#d0a24f]/10 rounded-2xl flex items-center justify-center">
                    <i className="ri-user-star-line text-3xl text-[#d0a24f]"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-[#444c42] mb-3 font-['Montserrat']">Elite Invisalign Provider with Proven Results</h3>
                  <p className="text-[#7A8178] leading-relaxed">As an <strong>Invisalign Elite Provider</strong>, Dr George has successfully treated over 2000 <strong>Invisalign cases</strong>, from simple alignment to complex bite correction, with a strong focus on aesthetics, comfort, and long-term stability.</p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-[#d0a24f]/10 rounded-2xl flex items-center justify-center">
                    <i className="ri-computer-line text-3xl text-[#d0a24f]"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-[#444c42] mb-3 font-['Montserrat']">Precision-Led, Digitally Planned Treatment</h3>
                  <p className="text-[#7A8178] leading-relaxed">Every <strong>Invisalign journey</strong> begins with advanced <strong>3D digital scanning</strong> and bespoke treatment planning. This allows you to preview your expected results and ensures your aligners are designed with accuracy, efficiency, and comfort in mind.</p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-[#d0a24f]/10 rounded-2xl flex items-center justify-center">
                    <i className="ri-calendar-check-line text-3xl text-[#d0a24f]"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-[#444c42] mb-3 font-['Montserrat']">Flexible Appointments &amp; 0% Interest-Free Finance</h3>
                  <p className="text-[#7A8178] leading-relaxed">Enjoy fewer visits compared to traditional braces, flexible appointment scheduling, and <strong>0% interest-free finance options</strong>, making premium <strong>Invisalign treatment</strong> accessible and convenient.</p>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://static.readdy.ai/image/0a3008c36638439fa5d134c9c6a19be7/53653cdf2b35bb5f2a6a063d9fc49aa5.jpeg"
                  alt="Dr George Papadakis Diamond Apex Award-winning Invisalign Elite Provider Beaulieu Dental Springfield Chelmsford"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-8 -left-8 bg-[#d0a24f] text-white p-8 rounded-2xl shadow-xl max-w-xs">
                <div className="text-5xl font-bold mb-2 font-['Montserrat']">Diamond</div>
                <p className="text-white/90 font-medium">Apex Award Winner</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stunning Invisalign Transformations - Updated with Slider */}
      <section id="transformations" className="py-16 bg-gradient-to-b from-gray-50 to-white scroll-animate">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#444c42] mb-6 font-['Montserrat']">
              Stunning <strong>Invisalign Transformations</strong>
            </h2>
            <p className="text-xl text-[#7A8178] max-w-3xl mx-auto">
              See the incredible results our patients have achieved with <strong>Invisalign treatment at Beaulieu Dental Chelmsford</strong>
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Case Study 1 */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all">
              <div 
                className="before-after-slider relative w-full h-80"
                onMouseMove={(e) => handleSliderMove(0, e)}
                onTouchMove={(e) => handleSliderMove(0, e)}
                onMouseDown={() => handleSliderStart(0)}
                onTouchStart={() => handleSliderStart(0)}
              >
                {/* After Image (Background) */}
                <div className="absolute inset-0">
                  <img 
                    src="https://static.readdy.ai/image/0a3008c36638439fa5d134c9c6a19be7/edf777b6c9dfa24f34b284c3ecc24072.jpeg"
                    alt="After Invisalign Treatment Chelmsford"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-full text-sm font-bold z-10 font-['Montserrat']">
                    After
                  </div>
                </div>
                
                {/* Before Image (Overlay with clip) */}
                <div 
                  className="absolute inset-0"
                  style={{ clipPath: `inset(0 ${100 - sliderPositions[0]}% 0 0)` }}
                >
                  <img 
                    src="https://static.readdy.ai/image/0a3008c36638439fa5d134c9c6a19be7/2bf8caf557e3a045dced9fb14b3cf806.jpeg"
                    alt="Before Invisalign Treatment Chelmsford"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-4 left-4 bg-red-500 text-white px-4 py-2 rounded-full text-sm font-bold z-10 font-['Montserrat']">
                    Before
                  </div>
                </div>

                {/* Slider Handle */}
                <div 
                  className="absolute top-0 bottom-0 w-1 bg-white shadow-lg z-20"
                  style={{ left: `${sliderPositions[0]}%` }}
                >
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-xl flex items-center justify-center">
                    <div className="flex gap-1">
                      <i className="ri-arrow-left-s-line text-gray-600"></i>
                      <i className="ri-arrow-right-s-line text-gray-600"></i>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <h4 className="font-bold text-xl text-[#444c42] mb-2 font-['Montserrat']">Case Study 1: Crowding &amp; Gaps</h4>
                <p className="text-[#7A8178] mb-4">Treatment Duration: 9 months</p>
                <p className="text-[#7A8178]">Severe crowding and spacing issues corrected with <strong>Invisalign</strong>, resulting in a perfectly aligned, confident smile.</p>
              </div>
            </div>

            {/* Case Study 2 */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all">
              <div 
                className="before-after-slider relative w-full h-80"
                onMouseMove={(e) => handleSliderMove(1, e)}
                onTouchMove={(e) => handleSliderMove(1, e)}
                onMouseDown={() => handleSliderStart(1)}
                onTouchStart={() => handleSliderStart(1)}
              >
                {/* After Image (Background) */}
                <div className="absolute inset-0">
                  <img 
                    src="https://static.readdy.ai/image/0a3008c36638439fa5d134c9c6a19be7/d693b29cd327068e5a2526246866c85e.jpeg"
                    alt="After Invisalign Overbite Treatment"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-full text-sm font-bold z-10 font-['Montserrat']">
                    After
                  </div>
                </div>
                
                {/* Before Image (Overlay with clip) */}
                <div 
                  className="absolute inset-0"
                  style={{ clipPath: `inset(0 ${100 - sliderPositions[1]}% 0 0)` }}
                >
                  <img 
                    src="https://static.readdy.ai/image/0a3008c36638439fa5d134c9c6a19be7/36f85b008151046aaaeaa04fa6017186.jpeg"
                    alt="Before Invisalign Overbite Treatment"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-4 left-4 bg-red-500 text-white px-4 py-2 rounded-full text-sm font-bold z-10 font-['Montserrat']">
                    Before
                  </div>
                </div>

                {/* Slider Handle */}
                <div 
                  className="absolute top-0 bottom-0 w-1 bg-white shadow-lg z-20"
                  style={{ left: `${sliderPositions[1]}%` }}
                >
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-xl flex items-center justify-center">
                    <div className="flex gap-1">
                      <i className="ri-arrow-left-s-line text-gray-600"></i>
                      <i className="ri-arrow-right-s-line text-gray-600"></i>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <h4 className="font-bold text-xl text-[#444c42] mb-2 font-['Montserrat']">Case Study 2: Overbite Correction</h4>
                <p className="text-[#7A8178] mb-4">Treatment Duration: 11 months</p>
                <p className="text-[#7A8178]">Significant overbite corrected with <strong>Invisalign</strong>, improving both function and aesthetics for a healthier, more confident smile.</p>
              </div>
            </div>

            {/* Case Study 3 */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all">
              <div 
                className="before-after-slider relative w-full h-80"
                onMouseMove={(e) => handleSliderMove(2, e)}
                onTouchMove={(e) => handleSliderMove(2, e)}
                onMouseDown={() => handleSliderStart(2)}
                onTouchStart={() => handleSliderStart(2)}
              >
                {/* After Image (Background) */}
                <div className="absolute inset-0">
                  <img 
                    src="https://static.readdy.ai/image/0a3008c36638439fa5d134c9c6a19be7/6b649708d1f8586719defd239dc29437.jpeg"
                    alt="After Invisalign Treatment Beaulieu Dental"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-full text-sm font-bold z-10 font-['Montserrat']">
                    After
                  </div>
                </div>
                
                {/* Before Image (Overlay with clip) */}
                <div 
                  className="absolute inset-0"
                  style={{ clipPath: `inset(0 ${100 - sliderPositions[2]}% 0 0)` }}
                >
                  <img 
                    src="https://static.readdy.ai/image/0a3008c36638439fa5d134c9c6a19be7/ff6f0ecacfa7047b4f3f03681ec346b3.jpeg"
                    alt="Before Invisalign Treatment Beaulieu Dental"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-4 left-4 bg-red-500 text-white px-4 py-2 rounded-full text-sm font-bold z-10 font-['Montserrat']">
                    Before
                  </div>
                </div>

                {/* Slider Handle */}
                <div 
                  className="absolute top-0 bottom-0 w-1 bg-white shadow-lg z-20"
                  style={{ left: `${sliderPositions[2]}%` }}
                >
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-xl flex items-center justify-center">
                    <div className="flex gap-1">
                      <i className="ri-arrow-left-s-line text-gray-600"></i>
                      <i className="ri-arrow-right-s-line text-gray-600"></i>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <h4 className="font-bold text-xl text-[#444c42] mb-2 font-['Montserrat']">Case Study 3: Rotated Teeth</h4>
                <p className="text-[#7A8178] mb-4">Treatment Duration: 8 months</p>
                <p className="text-[#7A8178]">Multiple rotated teeth straightened efficiently, creating a harmonious and aesthetically pleasing smile.</p>
              </div>
            </div>

            {/* Case Study 4 */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all">
              <div 
                className="before-after-slider relative w-full h-80"
                onMouseMove={(e) => handleSliderMove(3, e)}
                onTouchMove={(e) => handleSliderMove(3, e)}
                onMouseDown={() => handleSliderStart(3)}
                onTouchStart={() => handleSliderStart(3)}
              >
                {/* After Image (Background) */}
                <div className="absolute inset-0">
                  <img 
                    src="https://static.readdy.ai/image/0a3008c36638439fa5d134c9c6a19be7/4da2b59e9dd73e6942efa1c560d7cb3e.jpeg"
                    alt="After Invisalign Chelmsford Essex"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-full text-sm font-bold z-10 font-['Montserrat']">
                    After
                  </div>
                </div>
                
                {/* Before Image (Overlay with clip) */}
                <div 
                  className="absolute inset-0"
                  style={{ clipPath: `inset(0 ${100 - sliderPositions[3]}% 0 0)` }}
                >
                  <img 
                    src="https://static.readdy.ai/image/0a3008c36638439fa5d134c9c6a19be7/b9a49fd7b3be5f9c72c9c50ae0cd9b63.jpeg"
                    alt="Before Invisalign Chelmsford Essex"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-4 left-4 bg-red-500 text-white px-4 py-2 rounded-full text-sm font-bold z-10 font-['Montserrat']">
                    Before
                  </div>
                </div>

                {/* Slider Handle */}
                <div 
                  className="absolute top-0 bottom-0 w-1 bg-white shadow-lg z-20"
                  style={{ left: `${sliderPositions[3]}%` }}
                >
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-xl flex items-center justify-center">
                    <div className="flex gap-1">
                      <i className="ri-arrow-left-s-line text-gray-600"></i>
                      <i className="ri-arrow-right-s-line text-gray-600"></i>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <h4 className="font-bold text-xl text-[#444c42] mb-2 font-['Montserrat']">Case Study 4: Crowding</h4>
                <p className="text-[#7A8178] mb-4">Treatment Duration: 9 months</p>
                <p className="text-[#7A8178]">Crowded and rotated teeth were carefully corrected using Invisalign, creating a straighter, more even smile. This treatment improved alignment, comfort, and overall appearance, delivering a confident, natural result.</p>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-[#7A8178] mb-6 text-lg">Ready to see your own transformation?</p>
            <a href="#consultation" className="inline-block bg-[#d0a24f] text-white px-8 py-4 rounded-full text-lg font-bold hover:bg-[#dcb964] transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap shadow-xl font-['Montserrat']">
              Schedule Your Free Consultation
            </a>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section id="features" className="py-16 bg-white scroll-animate">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#444c42] mb-6 font-['Montserrat']">
              The <strong>Beaulieu Dental Invisalign Advantage</strong>
            </h2>
            <p className="text-xl text-[#7A8178] max-w-3xl mx-auto leading-relaxed">
              At <strong>Beaulieu Dental</strong>, <strong>Invisalign treatment</strong> is delivered using advanced digital technology and a precision-led clinical approach — ensuring comfort, efficiency, and exceptional results from start to finish.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 mb-12">
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-10 border border-gray-100 hover:shadow-xl transition-all transform hover:scale-105 duration-500">
              <div className="w-20 h-20 bg-[#d0a24f] rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-computer-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#444c42] mb-4 font-['Montserrat']">Digitally Planned, Precision-Led Care</h3>
              <p className="text-[#7A8178] leading-relaxed mb-6">
                Your <strong>Invisalign journey</strong> begins with <strong>iTero Lumina 3D digital scanning</strong>, providing fast, highly accurate results with no messy impressions. Using advanced digital planning, you can preview your future smile before treatment even begins, giving you clarity and confidence from day one.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#d0a24f] text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-[#7A8178] text-sm"><strong>iTero Lumina 3D scanning</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#d0a24f] text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-[#7A8178] text-sm">Smile preview before treatment</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#d0a24f] text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-[#7A8178] text-sm">Predictable, digitally planned outcomes</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-10 border border-gray-100 hover:shadow-xl transition-all transform hover:scale-105 duration-500">
              <div className="w-20 h-20 bg-[#d0a24f] rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-user-heart-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#444c42] mb-4 font-['Montserrat']">Custom Invisalign Aligners</h3>
              <p className="text-[#7A8178] leading-relaxed mb-6">
                Your aligners are custom-made specifically for your teeth and treatment goals, ensuring precise movement, improved comfort, and natural-looking results.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#d0a24f] text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-[#7A8178] text-sm">Bespoke <strong>Invisalign aligners</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#d0a24f] text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-[#7A8178] text-sm">Tailored to your bite and smile goals</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#d0a24f] text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-[#7A8178] text-sm">Gentle, controlled tooth movement</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-10 border border-gray-100 hover:shadow-xl transition-all transform hover:scale-105 duration-500">
              <div className="w-20 h-20 bg-[#d0a24f] rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-heart-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#444c42] mb-4 font-['Montserrat']">Comfortable, Lifestyle-Friendly Treatment</h3>
              <p className="text-[#7A8178] leading-relaxed mb-6">
                <strong>Invisalign aligners</strong> are smooth, discreet, and removable — allowing you to eat, drink, brush, and floss as normal throughout treatment.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#d0a24f] text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-[#7A8178] text-sm">Virtually invisible in daily life</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#d0a24f] text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-[#7A8178] text-sm">No brackets or wires</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#d0a24f] text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-[#7A8178] text-sm">Easy oral hygiene and flexibility</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-10 border border-gray-100 hover:shadow-xl transition-all transform hover:scale-105 duration-500">
              <div className="w-20 h-20 bg-[#d0a24f] rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-star-smile-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#444c42] mb-4 font-['Montserrat']">Complete Smile Transformation</h3>
              <p className="text-[#7A8178] leading-relaxed mb-6">
                To ensure your results last and your smile looks its best, your <strong>Invisalign treatment at Beaulieu Dental</strong> includes custom retainers and <strong>FREE professional teeth whitening</strong> once treatment is complete.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#d0a24f] text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-[#7A8178] text-sm">Custom retainers included</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#d0a24f] text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-[#7A8178] text-sm"><strong>FREE professional whitening</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#d0a24f] text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-[#7A8178] text-sm">Long-lasting, confident results</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-gradient-to-br from-[#d0a24f]/5 to-white rounded-3xl p-10 border-2 border-[#d0a24f]/20 shadow-lg">
            <div className="text-center">
              <div className="w-20 h-20 bg-[#d0a24f] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-trophy-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#444c42] mb-4 font-['Montserrat']">Expert-Led Results You Can Trust</h3>
              <p className="text-lg text-[#7A8178] leading-relaxed mb-6 max-w-3xl mx-auto">
                Your <strong>Invisalign treatment</strong> is overseen by <strong>Dr George Papadakis</strong>, a <strong>Diamond Apex Award-winning Invisalign Elite Provider</strong>, recognised for delivering precise, natural smile transformations with consistent results.
              </p>
              <div className="flex flex-wrap justify-center items-center gap-4 text-sm text-[#7A8178] font-semibold">
                <span className="flex items-center gap-2">
                  <i className="ri-award-line text-[#d0a24f] text-lg"></i>
                  Diamond Apex Award Winner
                </span>
                <span className="text-[#d0a24f]">•</span>
                <span className="flex items-center gap-2">
                  <i className="ri-user-star-line text-[#d0a24f] text-lg"></i>
                  Invisalign Elite Provider
                </span>
                <span className="text-[#d0a24f]">•</span>
                <span className="flex items-center gap-2">
                  <i className="ri-computer-line text-[#d0a24f] text-lg"></i>
                  Advanced Digital Orthodontics in Chelmsford
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Your Invisalign Journey Banner - Updated Background */}
      <section className="py-16 bg-gradient-to-br from-[#444c42] to-[#7A8178] relative overflow-hidden scroll-animate">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=Professional%20dental%20clinic%20scene%20showing%20dentist%20carefully%20placing%20clear%20Invisalign%20aligners%20on%20patient%20teeth%20during%20orthodontic%20treatment%20consultation%2C%20modern%20UK%20dental%20practice%20interior%20with%20advanced%20digital%20equipment%20and%20comfortable%20patient%20chair%2C%20soft%20clinical%20lighting%20highlighting%20precision%20dental%20care%20and%20patient%20comfort%20in%20Chelmsford%20orthodontic%20practice&width=1920&height=600&seq=invisalign-journey-treatment-banner&orientation=landscape"
            alt="Invisalign Treatment Journey"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-['Montserrat']">
              Your <strong>Invisalign Journey</strong> at Beaulieu Dental
            </h2>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              From consultation to your dream smile, we guide you every step of the way
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all">
              <div className="w-16 h-16 bg-[#d0a24f] rounded-full flex items-center justify-center mb-6 mx-auto">
                <span className="text-3xl font-bold text-white font-['Montserrat']">1</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-4 text-center font-['Montserrat']">Free Consultation</h3>
              <p className="text-white/80 text-center">Comprehensive assessment and 3D digital scan of your teeth</p>
            </div>

            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all">
              <div className="w-16 h-16 bg-[#d0a24f] rounded-full flex items-center justify-center mb-6 mx-auto">
                <span className="text-3xl font-bold text-white font-['Montserrat']">2</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-4 text-center font-['Montserrat']">Custom Treatment Plan</h3>
              <p className="text-white/80 text-center">Preview your future smile and receive detailed timeline</p>
            </div>

            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all">
              <div className="w-16 h-16 bg-[#d0a24f] rounded-full flex items-center justify-center mb-6 mx-auto">
                <span className="text-3xl font-bold text-white font-['Montserrat']">3</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-4 text-center font-['Montserrat']">Begin Treatment</h3>
              <p className="text-white/80 text-center">Receive your custom aligners and start your transformation</p>
            </div>

            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all">
              <div className="w-16 h-16 bg-[#d0a24f] rounded-full flex items-center justify-center mb-6 mx-auto">
                <span className="text-3xl font-bold text-white font-['Montserrat']">4</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-4 text-center font-['Montserrat']">Reveal Your Smile</h3>
              <p className="text-white/80 text-center">Complete treatment with FREE whitening and custom retainers</p>
            </div>
          </div>

          <div className="text-center mt-12">
            <a href="#consultation" className="inline-block bg-[#d0a24f] text-white px-8 py-4 rounded-full text-lg font-bold hover:bg-[#dcb964] transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap shadow-xl font-['Montserrat']">
              Start Your Journey Today
            </a>
          </div>
        </div>
      </section>

      {/* Free Parking Section */}
      <section className="py-12 bg-gradient-to-br from-[#7A8178]/10 to-white scroll-animate">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 border-2 border-[#d0a24f]/20">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="w-24 h-24 bg-[#d0a24f] rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-parking-box-line text-5xl text-white"></i>
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-3xl font-bold text-[#444c42] mb-4 font-['Montserrat']">Free Onsite Parking Available</h3>
                <p className="text-lg text-[#7A8178] leading-relaxed">
                  We offer <strong>three hours of complimentary onsite parking</strong> in our large, spacious car park at <strong>Beaulieu Square Springfield</strong>, making every visit to our <strong>Chelmsford dental clinic</strong> stress-free and convenient.
                </p>
              </div>
              <div className="flex-shrink-0">
                <div className="bg-[#d0a24f]/10 rounded-2xl p-6 text-center">
                  <div className="text-5xl font-bold text-[#d0a24f] mb-2 font-['Montserrat']">3 Hours</div>
                  <p className="text-[#7A8178] font-semibold">Free Parking</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Free Consultation Form Section */}
      <section id="consultation" className="py-16 bg-gradient-to-br from-[#d0a24f]/5 to-white scroll-animate">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#444c42] mb-6 font-['Montserrat']">
                Start Your <strong>Smile Journey</strong> Today
              </h2>
              <p className="text-xl text-[#7A8178] mb-8 leading-relaxed">
                Book your complimentary <strong>Invisalign consultation in Chelmsford</strong> and discover how we can transform your smile. Our expert team at <strong>Beaulieu Dental Springfield</strong> will assess your needs and create a personalized treatment plan just for you.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#d0a24f] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-checkbox-circle-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-[#444c42] text-lg mb-2 font-['Montserrat']">Comprehensive Assessment</h4>
                    <p className="text-[#7A8178]">Complete oral examination and 3D digital scan of your teeth</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#d0a24f] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-eye-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-[#444c42] text-lg mb-2 font-['Montserrat']">Preview Your Results</h4>
                    <p className="text-[#7A8178]">See a virtual simulation of your future smile before starting</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#d0a24f] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-file-text-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-[#444c42] text-lg mb-2 font-['Montserrat']">Custom Treatment Plan</h4>
                    <p className="text-[#7A8178]">Receive a detailed plan with timeline and cost breakdown</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-6 bg-white rounded-2xl border-2 border-[#d0a24f]/20 shadow-lg">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-[#d0a24f]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-percent-line text-3xl text-[#d0a24f]"></i>
                  </div>
                  <div>
                    <p className="font-bold text-[#444c42] text-lg font-['Montserrat']">0% Interest-Free Finance</p>
                    <p className="text-[#7A8178]">Flexible payment plans available to make your <strong>Invisalign treatment</strong> affordable</p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-16 bg-white scroll-animate">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#444c42] mb-6 font-['Montserrat']">
              Real Stories, Real Smiles
            </h2>
            <p className="text-xl text-[#7A8178] max-w-3xl mx-auto">
              Hear from our satisfied patients who transformed their lives with <strong>Invisalign at Beaulieu Dental Springfield Chelmsford</strong>
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 border border-gray-100 hover:shadow-xl transition-all">
              <div className="flex items-center gap-1 mb-6">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#d0a24f] text-xl"></i>
                ))}
              </div>
              <p className="text-[#7A8178] leading-relaxed mb-6 italic">
                "I was hesitant about getting braces as an adult, but <strong>Invisalign</strong> was perfect! No one even noticed I was wearing them. The team at <strong>Beaulieu Dental</strong> made the entire process smooth and comfortable. My smile has never looked better!"
              </p>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-[#d0a24f]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-[#d0a24f] font-bold text-lg font-['Montserrat']">SJ</span>
                </div>
                <div>
                  <p className="font-bold text-[#444c42] font-['Montserrat']">Sarah Johnson</p>
                  <p className="text-sm text-[#7A8178]">Marketing Executive</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 border border-gray-100 hover:shadow-xl transition-all">
              <div className="flex items-center gap-1 mb-6">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#d0a24f] text-xl"></i>
                ))}
              </div>
              <p className="text-[#7A8178] leading-relaxed mb-6 italic">
                "As a teacher, I needed something discreet. <strong>Invisalign</strong> was the answer! The 3D preview showed me exactly what to expect, and the results exceeded my expectations. The staff at <strong>Beaulieu Dental Springfield</strong> is incredibly professional and caring."
              </p>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-[#d0a24f]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-[#d0a24f] font-bold text-lg font-['Montserrat']">MC</span>
                </div>
                <div>
                  <p className="font-bold text-[#444c42] font-['Montserrat']">Michael Chen</p>
                  <p className="text-sm text-[#7A8178]">High School Teacher</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 border border-gray-100 hover:shadow-xl transition-all">
              <div className="flex items-center gap-1 mb-6">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#d0a24f] text-xl"></i>
                ))}
              </div>
              <p className="text-[#7A8178] leading-relaxed mb-6 italic">
                "I love that I could remove the aligners for important meetings and meals. The treatment was faster than I expected - just 10 months! The team were amazing throughout the entire journey. Highly recommend <strong>Beaulieu Dental Chelmsford</strong>!"
              </p>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-[#d0a24f]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-[#d0a24f] font-bold text-lg font-['Montserrat']">EP</span>
                </div>
                <div>
                  <p className="font-bold text-[#444c42] font-['Montserrat']">Emily Parker</p>
                  <p className="text-sm text-[#7A8178]">Business Owner</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 border border-gray-100 hover:shadow-xl transition-all">
              <div className="flex items-center gap-1 mb-6">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#d0a24f] text-xl"></i>
                ))}
              </div>
              <p className="text-[#7A8178] leading-relaxed mb-6 italic">
                "After years of being self-conscious about my smile, I finally took the leap with <strong>Invisalign</strong>. The comfort level compared to traditional braces is incredible. I'm so grateful to <strong>Beaulieu Dental</strong> for giving me confidence again!"
              </p>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-[#d0a24f]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-[#d0a24f] font-bold text-lg font-['Montserrat']">DT</span>
                </div>
                <div>
                  <p className="font-bold text-[#444c42] font-['Montserrat']">David Thompson</p>
                  <p className="text-sm text-[#7A8178]">Software Engineer</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 border border-gray-100 hover:shadow-xl transition-all">
              <div className="flex items-center gap-1 mb-6">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#d0a24f] text-xl"></i>
                ))}
              </div>
              <p className="text-[#7A8178] leading-relaxed mb-6 italic">
                "The <strong>flexible payment plans</strong> made <strong>Invisalign</strong> affordable for me. The entire process was transparent, and I always knew what to expect. My teeth are perfectly aligned now, and I couldn't be happier with the results!"
              </p>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-[#d0a24f]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-[#d0a24f] font-bold text-lg font-['Montserrat']">JM</span>
                </div>
                <div>
                  <p className="font-bold text-[#444c42] font-['Montserrat']">Jessica Martinez</p>
                  <p className="text-sm text-[#7A8178]">Nurse Practitioner</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 border border-gray-100 hover:shadow-xl transition-all">
              <div className="flex items-center gap-1 mb-6">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#d0a24f] text-xl"></i>
                ))}
              </div>
              <p className="text-[#7A8178] leading-relaxed mb-6 italic">
                "From consultation to completion, everything was handled professionally. The technology they use is impressive - seeing my smile transformation before it happened was mind-blowing. Worth every penny at <strong>Beaulieu Dental Springfield</strong>!"
              </p>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-[#d0a24f]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-[#d0a24f] font-bold text-lg font-['Montserrat']">RW</span>
                </div>
                <div>
                  <p className="font-bold text-[#444c42] font-['Montserrat']">Robert Williams</p>
                  <p className="text-sm text-[#7A8178]">Financial Advisor</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 bg-white scroll-animate">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#444c42] mb-6 font-['Montserrat']">
              Visit <strong>Beaulieu Dental Springfield</strong>
            </h2>
            <p className="text-xl text-[#7A8178] max-w-3xl mx-auto">
              We're here to answer your questions and help you start your <strong>Invisalign smile transformation journey in Chelmsford</strong>
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <div className="bg-gradient-to-br from-[#d0a24f]/5 to-white rounded-3xl p-8 border border-[#d0a24f]/20 mb-8">
                <h3 className="text-2xl font-bold text-[#444c42] mb-6 font-['Montserrat']">Get in Touch</h3>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#d0a24f] rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-xl text-white"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#444c42] mb-1 font-['Montserrat']">Address</h4>
                      <p className="text-[#7A8178]"><strong>Beaulieu Dental</strong><br />11 Centenary Way<br />Beaulieu Square, Springfield<br />Chelmsford, Essex CM1 6AU</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#d0a24f] rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-xl text-white"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#444c42] mb-1 font-['Montserrat']">Phone</h4>
                      <p className="text-[#7A8178]"><a href="tel:01245465586" className="hover:text-[#d0a24f] transition-colors">01245 465586</a></p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#d0a24f] rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-whatsapp-line text-xl text-white"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#444c42] mb-1 font-['Montserrat']">WhatsApp</h4>
                      <p className="text-[#7A8178]"><a href="https://wa.me/447426343948" target="_blank" rel="noopener noreferrer" className="hover:text-[#d0a24f] transition-colors">07426 343948</a></p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#d0a24f] rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-xl text-white"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#444c42] mb-1 font-['Montserrat']">Email</h4>
                      <p className="text-[#7A8178]"><a href="mailto:appointments@beaulieudental.co.uk" className="hover:text-[#d0a24f] transition-colors">appointments@beaulieudental.co.uk</a></p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#d0a24f] rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-global-line text-xl text-white"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#444c42] mb-1 font-['Montserrat']">Main Website</h4>
                      <p className="text-[#7A8178]"><a href="https://www.beaulieudental.co.uk" target="_blank" rel="noopener noreferrer" className="hover:text-[#d0a24f] transition-colors">www.beaulieudental.co.uk</a></p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-8 border-t border-[#d0a24f]/20">
                  <h4 className="font-bold text-[#444c42] mb-4 font-['Montserrat']">Follow Us</h4>
                  <div className="flex gap-4">
                    <a href="#" className="w-12 h-12 bg-[#d0a24f] rounded-full flex items-center justify-center hover:bg-[#dcb964] transition-colors cursor-pointer">
                      <i className="ri-facebook-fill text-xl text-white"></i>
                    </a>
                    <a href="#" className="w-12 h-12 bg-[#d0a24f] rounded-full flex items-center justify-center hover:bg-[#dcb964] transition-colors cursor-pointer">
                      <i className="ri-instagram-line text-xl text-white"></i>
                    </a>
                    <a href="#" className="w-12 h-12 bg-[#d0a24f] rounded-full flex items-center justify-center hover:bg-[#dcb964] transition-colors cursor-pointer">
                      <i className="ri-twitter-x-line text-xl text-white"></i>
                    </a>
                    <a href="#" className="w-12 h-12 bg-[#d0a24f] rounded-full flex items-center justify-center hover:bg-[#dcb964] transition-colors cursor-pointer">
                      <i className="ri-linkedin-fill text-xl text-white"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="bg-gradient-to-br from-[#d0a24f]/5 to-white rounded-3xl p-8 border border-[#d0a24f]/20 mb-8">
                <h3 className="text-2xl font-bold text-[#444c42] mb-6 font-['Montserrat']">Opening Hours</h3>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b border-[#d0a24f]/10">
                    <span className="text-[#444c42] font-semibold">Monday</span>
                    <span className="text-[#7A8178]">9:00 AM – 5:00 PM</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-[#d0a24f]/10">
                    <span className="text-[#444c42] font-semibold">Tuesday</span>
                    <span className="text-[#7A8178]">9:00 AM – 5:00 PM</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-[#d0a24f]/10">
                    <span className="text-[#444c42] font-semibold">Wednesday</span>
                    <span className="text-[#7A8178]">9:00 AM – 5:00 PM</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-[#d0a24f]/10">
                    <span className="text-[#444c42] font-semibold">Thursday</span>
                    <span className="text-[#7A8178]">8:00 AM – 5:00 PM</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-[#d0a24f]/10">
                    <span className="text-[#444c42] font-semibold">Friday</span>
                    <span className="text-[#7A8178]">8:00 AM – 1:00 PM</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-[#d0a24f]/10">
                    <span className="text-[#444c42] font-semibold">Saturdays</span>
                    <span className="text-[#7A8178]">8:00 AM – 1:00 PM <span className="text-sm">(Alternate)</span></span>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-red-50 to-white rounded-3xl p-6 border-2 border-red-200 shadow-lg">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-alarm-warning-line text-3xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-[#444c42] mb-2 font-['Montserrat']">Emergency Dental Care</h3>
                    <p className="text-[#7A8178] leading-relaxed">
                      Out-of-hours emergency services are available, subject to availability. Additional call-out fees apply.
                    </p>
                  </div>
                </div>
                
                <div className="bg-white rounded-2xl p-6 border border-red-100">
                  <div className="flex items-center gap-3 mb-4">
                    <i className="ri-phone-line text-2xl text-red-500"></i>
                    <div>
                      <p className="text-sm text-[#7A8178] mb-1">Emergency Contact</p>
                      <a href="tel:01245465586" className="text-xl font-bold text-[#444c42] hover:text-[#d0a24f] transition-colors">01245 465586</a>
                    </div>
                  </div>
                  <p className="text-sm text-[#7A8178]">
                    For urgent dental emergencies outside regular hours, please call our main number and follow the instructions for emergency care.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-[#444c42] to-[#7A8178] text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <img 
                src="https://static.readdy.ai/image/0a3008c36638439fa5d134c9c6a19be7/f2c19796cf6e2cf808b2267d17a34318.png" 
                alt="Beaulieu Dental Logo" 
                className="h-16 w-auto mb-6 brightness-0 invert"
              />
              <p className="text-white/80 leading-relaxed">
                Your trusted partner for beautiful, healthy smiles. Specializing in <strong>Invisalign</strong> and comprehensive dental care in <strong>Springfield, Chelmsford</strong>.
              </p>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-6 font-['Montserrat']">Quick Links</h4>
              <ul className="space-y-3">
                <li><a href="#value" className="text-white/80 hover:text-white transition-colors cursor-pointer">Why Invisalign</a></li>
                <li><a href="#features" className="text-white/80 hover:text-white transition-colors cursor-pointer">Features</a></li>
                <li><a href="#testimonials" className="text-white/80 hover:text-white transition-colors cursor-pointer">Testimonials</a></li>
                <li><a href="#consultation" className="text-white/80 hover:text-white transition-colors cursor-pointer">Book Consultation</a></li>
                <li><a href="https://beaulieudental.co.uk/treatments/invisalign/" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white transition-colors cursor-pointer">Main Website</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-6 font-['Montserrat']">Contact</h4>
              <ul className="space-y-3 text-white/80">
                <li>11 Centenary Way<br />Springfield, Chelmsford<br />Essex CM1 6AU</li>
                <li><a href="tel:01245465586" className="hover:text-white transition-colors">01245 465586</a></li>
                <li><a href="https://wa.me/447426343948" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">WhatsApp: 07426 343948</a></li>
                <li><a href="mailto:appointments@beaulieudental.co.uk" className="hover:text-white transition-colors">appointments@beaulieudental.co.uk</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-6 font-['Montserrat']">Office Hours</h4>
              <ul className="space-y-3 text-white/80 text-sm">
                <li className="flex justify-between gap-4">
                  <span>Monday - Wednesday:</span>
                  <span className="font-semibold whitespace-nowrap">9AM - 5PM</span>
                </li>
                <li className="flex justify-between gap-4">
                  <span>Thursday:</span>
                  <span className="font-semibold whitespace-nowrap">8AM - 5PM</span>
                </li>
                <li className="flex justify-between gap-4">
                  <span>Friday:</span>
                  <span className="font-semibold whitespace-nowrap">8AM - 1PM</span>
                </li>
                <li className="flex justify-between gap-4">
                  <span>Saturdays:</span>
                  <span className="font-semibold whitespace-nowrap">8AM - 1PM*</span>
                </li>
                <li className="text-xs text-white/60 mt-2">*Alternate Saturdays</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-white/80 text-sm">
                © 2026 Beaulieu Dental. All rights reserved. Designed by Elevareo
              </p>
              <div className="flex gap-6 text-sm">
                <a href="https://beaulieudental.co.uk/policies/" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white transition-colors cursor-pointer">Privacy Policy</a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Callback Modal */}
      {showCallbackModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowCallbackModal(false)}>
          <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-[#444c42] font-['Montserrat']">Request Call Back</h3>
              <button onClick={() => setShowCallbackModal(false)} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer flex-shrink-0">
                <i className="ri-close-line text-2xl text-[#444c42]"></i>
              </button>
            </div>
            
            <p className="text-[#7A8178] mb-6">Fill out the form below and we'll call you back within 24 hours</p>
            
            <form id="callback-form" data-readdy-form onSubmit={handleCallbackSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-[#444c42] mb-2 font-['Montserrat']">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={callbackFormData.name}
                  onChange={handleCallbackInputChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#d0a24f] focus:border-transparent transition-all text-sm"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-[#444c42] mb-2 font-['Montserrat']">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={callbackFormData.phone}
                  onChange={handleCallbackInputChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#d0a24f] focus:border-transparent transition-all text-sm"
                  placeholder="01245 465586"
                />
              </div>

              <div>
                <label htmlFor="preferredTime" className="block text-sm font-semibold text-[#444c42] mb-2 font-['Montserrat']">
                  Preferred Time
                </label>
                <select
                  id="preferredTime"
                  name="preferredTime"
                  value={callbackFormData.preferredTime}
                  onChange={handleCallbackInputChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#d0a24f] focus:border-transparent transition-all text-sm"
                >
                  <option value="">Select a time</option>
                  <option value="Morning (8AM-12PM)">Morning (8AM-12PM)</option>
                  <option value="Afternoon (12PM-4PM)">Afternoon (12PM-4PM)</option>
                  <option value="Evening (4PM-6PM)">Evening (4PM-6PM)</option>
                  <option value="Anytime">Anytime</option>
                </select>
              </div>

              <button
                type="submit"
                disabled={isCallbackSubmitting}
                className="w-full bg-[#d0a24f] text-white py-4 rounded-lg font-bold text-lg hover:bg-[#dcb964] transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none whitespace-nowrap shadow-lg font-['Montserrat']"
              >
                {isCallbackSubmitting ? 'Submitting...' : 'Request Call Back'}
              </button>

              {callbackSubmitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                  <strong>Success!</strong> We've received your request. We'll call you back within 24 hours.
                </div>
              )}

              {callbackSubmitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                  <strong>Error!</strong> Please fill in all required fields correctly.
                </div>
              )}
            </form>
          </div>
        </div>
      )}

      {/* Consultation Modal - Updated */}
      {showConsultationModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowConsultationModal(false)}>
          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-10 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-3xl font-bold text-[#444c42] font-['Montserrat']">Book Your Free Consultation</h3>
                <p className="text-[#7A8178] mt-2">Take the first step towards your perfect smile. Fill out the form below and we'll get back to you shortly.</p>
              </div>
              <button onClick={() => setShowConsultationModal(false)} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer flex-shrink-0">
                <i className="ri-close-line text-2xl text-[#444c42]"></i>
              </button>
            </div>
            
            <form id="consultation-modal-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-[#444c42] mb-2 font-['Montserrat']">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#d0a24f] focus:border-transparent transition-all text-sm"
                  placeholder="Enter your full name"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-[#444c42] mb-2 font-['Montserrat']">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#d0a24f] focus:border-transparent transition-all text-sm"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-[#444c42] mb-2 font-['Montserrat']">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#d0a24f] focus:border-transparent transition-all text-sm"
                    placeholder="01245 465586"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="whatsapp" className="block text-sm font-semibold text-[#444c42] mb-2 font-['Montserrat']">
                  WhatsApp Number (Optional)
                </label>
                <input
                  type="tel"
                  id="whatsapp"
                  name="whatsapp"
                  value={formData.whatsapp}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#d0a24f] focus:border-transparent transition-all text-sm"
                  placeholder="Enter WhatsApp number"
                />
                <p className="text-xs text-[#7A8178] mt-1">Available from 7:00 AM to 9:00 PM.</p>
              </div>

              <div>
                <label htmlFor="treatment" className="block text-sm font-semibold text-[#444c42] mb-2 font-['Montserrat']">
                  Treatment of Interest <span className="text-red-500">*</span>
                </label>
                <select
                  id="treatment"
                  name="treatment"
                  value={formData.treatment}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#d0a24f] focus:border-transparent transition-all text-sm"
                >
                  <option value="">Select a treatment</option>
                  <option value="Invisalign">Invisalign</option>
                  <option value="Teeth Whitening">Teeth Whitening</option>
                  <option value="Dental Implants">Dental Implants</option>
                  <option value="Veneer">Veneer</option>
                  <option value="General Checkup">General Checkup</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="date" className="block text-sm font-semibold text-[#444c42] mb-2 font-['Montserrat']">
                    Preferred Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    id="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#d0a24f] focus:border-transparent transition-all text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="time" className="block text-sm font-semibold text-[#444c42] mb-2 font-['Montserrat']">
                    Preferred Time to Call Back <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="time"
                    name="time"
                    value={formData.time}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#d0a24f] focus:border-transparent transition-all text-sm"
                  >
                    <option value="">Select a time</option>
                    <option value="Morning (8AM-12PM)">Morning (8AM-12PM)</option>
                    <option value="Afternoon (12PM-4PM)">Afternoon (12PM-4PM)</option>
                    <option value="Evening (4PM-6PM)">Evening (4PM-6PM)</option>
                  </select>
                  <p className="text-xs text-[#7A8178] mt-1">We're open until 5 PM (Mon-Thu), 1 PM (Fri), and on alternate Saturdays.</p>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-[#444c42] mb-2 font-['Montserrat']">
                  Additional Information
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#d0a24f] focus:border-transparent transition-all text-sm resize-none"
                  placeholder="Tell us about your dental concerns or what you'd like to discuss during the consultation..."
                ></textarea>
                <p className="text-xs text-[#7A8178] mt-1">Maximum 500 characters</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#14B8A6] text-white py-4 rounded-lg font-bold text-lg hover:bg-[#0d9488] transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none whitespace-nowrap shadow-lg font-['Montserrat']"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Consultation Request'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                  <strong>Success!</strong> We've received your consultation request. Our team will contact you within 24 hours.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                  <strong>Error!</strong> Please fill in all required fields correctly.
                </div>
              )}

              <p className="text-xs text-[#7A8178] text-center">
                By submitting this form, you agree to our privacy policy and consent to be contacted about <strong>Invisalign treatment</strong>.
              </p>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
